import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { motion } from 'framer-motion';
import { Share2 } from 'lucide-react';
import { CustomSelect } from '../components/CustomSelect';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get full image URL from relative or absolute path
const getImageUrl = (url) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  return `${BACKEND_URL}${url}`;
};

export const ApartmentsPage = () => {
  const { t, convertPrice, language } = useApp();
  const [apartments, setApartments] = useState([]);
  const [categories, setCategories] = useState([]);
  const [colorSettings, setColorSettings] = useState({
    status_available: '#22c55e',
    status_occupied: '#ef4444',
    status_available_soon: '#f59e0b',
    label_promo: '#facc15',
    label_recommended: '#3b82f6',
    label_new: '#10b981'
  });
  const [filters, setFilters] = useState({
    type: '',
    area: '',
    feature: '',
    status: '',
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCategories();
    fetchApartments();
    fetchColorSettings();
  }, []);

  const fetchColorSettings = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/settings/colors`);
      setColorSettings(data);
    } catch (error) {
      console.log('Using default colors');
    }
  };

  const fetchCategories = async () => {
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/categories`);
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories');
    }
  };

  const fetchApartments = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${BACKEND_URL}/api/apartments`);
      setApartments(data);
    } catch (error) {
      console.error('Failed to fetch apartments');
      toast.error('Failed to load apartments');
    } finally {
      setLoading(false);
    }
  };

  const getTranslatedText = (textObj) => {
    return textObj?.[language] || textObj?.en || '';
  };

  const getCategoryName = (catId) => {
    const cat = categories.find((c) => c.id === catId);
    return cat ? getTranslatedText(cat.name) : '';
  };

  const filteredApartments = apartments.filter((apt) => {
    if (filters.status && apt.status !== filters.status) return false;
    // Type filter - compare with apartment's apartment_type field (string)
    if (filters.type && apt.apartment_type !== filters.type) return false;
    
    // Area filter - need to match in ANY language since user might filter in different language than data was saved
    if (filters.area) {
      // Check if apartment's area matches the filter in any language
      const areaCat = categories.find(c => c.type === 'area' && (
        c.name?.ro === apt.area || 
        c.name?.en === apt.area || 
        c.name?.fr === apt.area ||
        c.name?.ro === filters.area ||
        c.name?.en === filters.area ||
        c.name?.fr === filters.area
      ));
      
      if (!areaCat) {
        // Direct comparison if no category match
        if (apt.area !== filters.area) return false;
      } else {
        // Check if apartment area matches any translation of the category
        const aptAreaMatches = apt.area === areaCat.name?.ro || 
                              apt.area === areaCat.name?.en || 
                              apt.area === areaCat.name?.fr;
        const filterMatches = filters.area === areaCat.name?.ro || 
                             filters.area === areaCat.name?.en || 
                             filters.area === areaCat.name?.fr;
        if (!aptAreaMatches || !filterMatches) return false;
      }
    }
    
    // Feature filter - check if apartment's features array includes the selected feature in any language
    if (filters.feature) {
      // Find the feature category that matches the filter
      const featureCat = categories.find(c => c.type === 'feature' && (
        c.name?.ro === filters.feature ||
        c.name?.en === filters.feature ||
        c.name?.fr === filters.feature
      ));
      
      if (featureCat) {
        // Check if any of the apartment's features match any translation of this category
        const hasFeature = apt.features.some(f => 
          f === featureCat.name?.ro || 
          f === featureCat.name?.en || 
          f === featureCat.name?.fr
        );
        if (!hasFeature) return false;
      } else {
        // Direct comparison if no category match
        if (!apt.features.includes(filters.feature)) return false;
      }
    }
    return true;
  });

  const handleShare = (apartment) => {
    const url = `${window.location.origin}/apartments/${apartment.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Link copied to clipboard!');
  };

  const getStatusBadge = (status, available_in_months = 0) => {
    if (status === 'available') {
      return (
        <span 
          className="px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded shadow-lg"
          style={{ backgroundColor: colorSettings.status_available }}
        >
          {t('available')}
        </span>
      );
    }
    if (status === 'available_soon') {
      const monthText = available_in_months === 1 
        ? (language === 'ro' ? 'lună' : language === 'fr' ? 'mois' : 'month')
        : (language === 'ro' ? 'luni' : language === 'fr' ? 'mois' : 'months');
      return (
        <span 
          className="px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded shadow-lg"
          style={{ backgroundColor: colorSettings.status_available_soon }}
        >
          {language === 'ro' ? `Disponibil în ${available_in_months} ${monthText}` 
            : language === 'fr' ? `Disponible dans ${available_in_months} ${monthText}`
            : `Available in ${available_in_months} ${monthText}`}
        </span>
      );
    }
    return (
      <span 
        className="px-3 py-1 text-white text-xs font-bold uppercase tracking-wider rounded shadow-lg"
        style={{ backgroundColor: colorSettings.status_occupied }}
      >
        {t('occupied')}
      </span>
    );
  };

  // Helper to determine text color based on background brightness
  const getContrastColor = (hexColor) => {
    const r = parseInt(hexColor.slice(1, 3), 16);
    const g = parseInt(hexColor.slice(3, 5), 16);
    const b = parseInt(hexColor.slice(5, 7), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000000' : '#ffffff';
  };

  const getLabels = (labels) => {
    if (!labels || labels.length === 0) return null;
    const colorMap = {
      promo: colorSettings.label_promo,
      recommended: colorSettings.label_recommended,
      new: colorSettings.label_new,
    };
    return (
      <div className="flex flex-col gap-1">
        {labels.map((labelText, index) => {
          const bgColor = colorMap[labelText] || '#6b7280';
          const textColor = getContrastColor(bgColor);
          return (
            <span 
              key={index}
              className="px-3 py-1 text-xs font-bold uppercase tracking-wider rounded shadow-lg"
              style={{ backgroundColor: bgColor, color: textColor }}
            >
              {labelText}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div data-testid="apartments-page" className="min-h-screen pt-32 pb-24">
      <div className="container mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-12"
        >
          <h1 data-testid="page-title" className="text-4xl md:text-5xl font-serif tracking-tight mb-4">
            {t('available_apartments')}
          </h1>
          <p className="text-lg text-muted-foreground">
            {language === 'ro'
              ? 'Explorați colecția noastră de apartamente elegante'
              : language === 'fr'
                ? 'Explorez notre collection d\'appartements élégants'
                : 'Explore our collection of elegant apartments'}
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="mb-8 glass p-4 md:p-6 relative z-20"
        >
          <h3 className="text-xs uppercase tracking-[0.2em] font-bold text-primary mb-3">{t('filters')}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4">
            <div>
              <label className="text-xs md:text-sm text-muted-foreground mb-1 block">{t('status')}</label>
              <CustomSelect
                value={filters.status || t('all')}
                onChange={(val) => setFilters({ ...filters, status: val === t('all') ? '' : val })}
                options={[
                  { value: t('all'), label: t('all') },
                  { value: 'available', label: t('available') },
                  { value: 'occupied', label: t('occupied') },
                ]}
                testId="filter-status"
              />
            </div>
            <div>
              <label className="text-xs md:text-sm text-muted-foreground mb-1 block">{t('property_type')}</label>
              <CustomSelect
                value={filters.type || t('all')}
                onChange={(val) => {
                  setFilters({ ...filters, type: val === t('all') ? '' : val });
                }}
                options={[
                  { value: t('all'), label: t('all') },
                  { value: 'studio', label: 'Studio' },
                  { value: '1_bedroom', label: '1 Bedroom' },
                  { value: '2_bedroom', label: '2 Bedrooms' },
                  { value: '3_bedroom', label: '3 Bedrooms' },
                  { value: 'apartment', label: 'Apartment' },
                  { value: 'house', label: 'House' },
                  { value: 'penthouse', label: 'Penthouse' },
                ]}
                testId="filter-type"
              />
            </div>
            <div>
              <label className="text-xs md:text-sm text-muted-foreground mb-1 block">{t('area')}</label>
              <CustomSelect
                value={filters.area || t('all')}
                onChange={(val) => {
                  setFilters({ ...filters, area: val === t('all') ? '' : val });
                }}
                options={[
                  { value: t('all'), label: t('all') },
                  ...categories
                    .filter((c) => c.type === 'area')
                    .map((cat) => ({
                      value: getTranslatedText(cat.name),
                      label: getTranslatedText(cat.name),
                    })),
                ]}
                testId="filter-area"
              />
            </div>
            <div>
              <label className="text-xs md:text-sm text-muted-foreground mb-1 block">{t('features')}</label>
              <CustomSelect
                value={filters.feature || t('all')}
                onChange={(val) => {
                  setFilters({ ...filters, feature: val === t('all') ? '' : val });
                }}
                options={[
                  { value: t('all'), label: t('all') },
                  ...categories
                    .filter((c) => c.type === 'feature')
                    .map((cat) => ({
                      value: getTranslatedText(cat.name),
                      label: getTranslatedText(cat.name),
                    })),
                ]}
                testId="filter-feature"
              />
            </div>
          </div>
        </motion.div>

        {/* Apartments Grid */}
        {loading ? (
          <div className="text-center py-24">
            <p className="text-muted-foreground">Loading apartments...</p>
          </div>
        ) : filteredApartments.length === 0 ? (
          <div className="text-center py-24">
            <p className="text-muted-foreground">No apartments found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative z-10">
            {filteredApartments.map((apartment, index) => (
              <motion.div
                key={apartment.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden bg-card border border-border hover:border-primary/50 transition-colors duration-500"
                data-testid={`apartment-card-${apartment.id}`}
              >
                {/* Image */}
                <Link to={`/apartments/${apartment.id}`} className="block">
                  <div className="relative h-64 overflow-hidden cursor-pointer">
                    <img
                      src={getImageUrl(apartment.images[0]) || 'https://via.placeholder.com/400x300?text=No+Image'}
                      alt={getTranslatedText(apartment.title)}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                    />
                    <div className="absolute top-4 right-4 flex flex-col gap-2 items-end">
                      {getLabels(apartment.labels)}
                      {getStatusBadge(apartment.status, apartment.available_in_months)}
                    </div>
                  </div>
                </Link>

                {/* Content */}
                <div className="p-6">
                  <Link to={`/apartments/${apartment.id}`} className="block">
                    <h3 className="text-2xl font-serif mb-2 group-hover:text-primary transition-colors cursor-pointer hover:underline">
                      {getTranslatedText(apartment.title)}
                    </h3>
                  </Link>
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {getTranslatedText(apartment.description)}
                  </p>

                  {/* Price */}
                  <div className="mb-4">
                    <span className="text-2xl font-serif text-primary">
                      {convertPrice(apartment.price_eur, apartment.price_ron)}
                    </span>
                    <span className="text-sm text-muted-foreground"> / month</span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-3">
                    <Link
                      to={`/apartments/${apartment.id}`}
                      data-testid={`view-details-${apartment.id}`}
                      className="inline-block w-full border border-primary/50 text-primary hover:bg-primary/10 px-6 py-3 text-center text-sm uppercase tracking-wider transition-all duration-300 rounded-full"
                    >
                      {t('view_details')}
                    </Link>
                    <button
                      data-testid={`share-button-${apartment.id}`}
                      onClick={() => handleShare(apartment)}
                      className="border border-white/20 text-foreground hover:bg-white/5 p-3 transition-all"
                    >
                      <Share2 size={16} />
                    </button>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ApartmentsPage;